// A dialog as wide as its content and no wider — so a row of fields sits on
// one line instead of wrapping into a tall dialog you have to scroll, and a
// short dialog does not stretch across the screen. Every dialog in the app
// uses it (2026-10-05); a new one should too.
//
//   <Dialog open={…} onClose={…} {...FIT_DIALOG}>
//   <Dialog open={…} onClose={…} {...fitDialog({ bgcolor: c.back })}>  // own paper styles
//
// ⚠️ TEXT MUST NOT SET THE WIDTH. `fit-content` sizes the paper to its widest
// child's natural width, and a paragraph's natural width is the whole paragraph
// on one line — so one long sentence would push the dialog to the edge of the
// screen. So prose is given `width: 0; min-width: 100%`: it takes whatever width
// the FIELDS decided and wraps inside it. That is applied automatically to
// Alerts, DialogContentText and any Typography sitting DIRECTLY in the dialog
// body; prose anywhere else gets the class `fit-text`.
//
// ⚠️ NOT FOR A FLEX ITEM. `min-width: 100%` on prose sitting BESIDE a button
// would take the whole row and push the button under it. Such text gets
// `width: 0, flex: '1 1 0', minWidth: <px>` instead — it then asks for only
// that minimum and grows into whatever the row has left.
//
// ⚠️ THE MINIMUM IS WHAT KEEPS A CONFIRMATION READABLE. A dialog holding one
// sentence and two buttons has almost no natural width; without a floor it
// would be a narrow column of wrapped words. FIT_DIALOG's floor is 400 px;
// FIT_XS / FIT_SM / FIT_MD keep MUI's 444 / 600 / 900 for a dialog that had
// that size before, so converting one can never make it narrower.

// MUI's own dialog widths. A converted dialog keeps its OLD width as its floor,
// so it can only grow to fit its data — never shrink into a taller, narrower
// box. (A form of fullWidth fields has almost no natural width of its own.)
const FLOOR = { xs: 444, sm: 600, md: 900 };
const floor = (px) => `min(${px}px, calc(100% - 32px))`;

const PROSE = [
  '& .MuiAlert-root',
  '& .MuiDialogContentText-root',
  '& .MuiDialogContent-root > .MuiTypography-root',
  '& .fit-text',
].join(', ');

/**
 * Dialog props for a fit-to-content dialog, merging any paper styles of its own.
 * @param size  the narrowest it may be: 'xs' | 'sm' | 'md' or a px number (default 400)
 */
export function fitDialog(paperSx = {}, size = 400) {
  const px = typeof size === 'number' ? size : (FLOOR[size] || 400);
  return {
    maxWidth: false,
    PaperProps: {
      sx: {
        width: 'fit-content',
        minWidth: floor(px),
        maxWidth: 'calc(100% - 32px)',
        [PROSE]: {
          width: 0,
          minWidth: '100%',
          boxSizing: 'border-box',
        },
        ...paperSx,
      },
    },
  };
}

export const FIT_DIALOG = fitDialog();
export const FIT_XS = fitDialog({}, 'xs');
export const FIT_SM = fitDialog({}, 'sm');
export const FIT_MD = fitDialog({}, 'md');

// ⚠️ A PHONE DIALOG LEAVES ROOM AT THE TOP AND THE BOTTOM (owner, 2026-10-26:
// "the company's battery details at the top, buttons at the bottom — leave space
// at top and bottom, keep this note for all times"). Never `fullScreen` on a
// phone: a sheet, full width less a margin, at most the screen height less the
// status bar, the navigation buttons and a gap above and below each. The body
// scrolls; the title and the action buttons stay in view.
//   <Dialog open={…} onClose={…} {...(phone ? PHONE_SHEET : FIT_SM)}>
export const PHONE_SHEET = {
  fullWidth: true,
  maxWidth: false,
  PaperProps: {
    sx: {
      m: 1,
      width: 'calc(100% - 16px)',
      maxHeight: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 96px)',
      borderRadius: 3,
    },
  },
};

export default FIT_DIALOG;
